import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const NAVY = "#0b192c";
const GOLD = "#c4a454";
const CREAM = "#fdfcf8";
const BORDER = "#e2e1dd";
const MUTED = "#475569";

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl sm:text-3xl mt-10 mb-3 leading-tight" style={{ color: NAVY }}>{children}</h2>
);
const P = ({ children }) => (
  <p className="text-sm sm:text-base mt-3 leading-relaxed" style={{ color: MUTED }}>{children}</p>
);
const UL = ({ children }) => (
  <ul className="mt-3 space-y-2 pl-5 list-disc text-sm sm:text-base leading-relaxed" style={{ color: MUTED }}>{children}</ul>
);

export default function Terms() {
  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="terms-page">
      <nav className="sticky top-0 z-40 border-b backdrop-blur-md" style={{ borderColor: BORDER, background: "rgba(253,252,248,0.9)" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/corpzo-icon-sm.png" alt="Corpzo" className="h-9 w-9" />
            <div className="font-serif text-xl leading-none" style={{ color: NAVY }}>
              Corpzo<span style={{ color: GOLD }}>.</span>
            </div>
          </Link>
          <Link to="/" className="text-sm flex items-center gap-2" style={{ color: MUTED }} data-testid="terms-back">
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="text-[11px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>Legal</div>
        <h1 className="font-serif text-4xl sm:text-6xl mt-4 leading-tight tracking-tight" style={{ color: NAVY }}>
          Terms of Service
        </h1>
        <p className="text-sm mt-4" style={{ color: MUTED }}>
          Last updated: February 2026 · These terms govern your use of AIFLedger by Corpzo.
        </p>

        <H2>1. Acceptance of Terms</H2>
        <P>
          By accessing or using AIFLedger by Corpzo (the &ldquo;Platform&rdquo;), operated by Corpzo · Fund Admin Ventures Private Limited
          (&ldquo;Corpzo&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;), you agree to be bound by these Terms of Service.
          If you do not agree, please do not use the Platform.
        </P>

        <H2>2. Nature of Service</H2>
        <P>
          AIFLedger is a fund-administration software platform for SEBI-registered Alternative Investment Funds (AIFs)
          in India. The Platform provides tools for investor onboarding, capital-call management, fund accounting, NAV
          computation, compliance tracking, and investor reporting. Corpzo is a technology service provider only and
          does not provide legal, tax, investment, or regulatory advice.
        </P>

        <H2>3. Eligibility & Account Registration</H2>
        <UL>
          <li>You must be authorised by your AIF, Investment Manager, Sponsor or Trustee to use the Platform.</li>
          <li>You are responsible for maintaining the confidentiality of your login credentials and MFA device.</li>
          <li>Any activity carried out under your account is deemed to be authorised by you.</li>
          <li>Investor / LP users may only access data relating to their own commitments and units.</li>
        </UL>

        <H2>4. Fund Data & Confidentiality</H2>
        <P>
          Data uploaded to the Platform by your fund (investor records, KYC, capital accounts, NAV, portfolio data, documents)
          remains the property of your fund. Corpzo processes this data solely to provide the Platform&apos;s services and
          maintains bank-grade access controls, audit logging and tenant isolation. We do not resell or share your data.
        </P>

        <H2>5. Prohibited Use</H2>
        <UL>
          <li>Attempting to breach access controls, escalate roles, or access another fund&apos;s data.</li>
          <li>Uploading malware, spam, or content that violates law or third-party rights.</li>
          <li>Using the Platform to solicit investment or offer securities in violation of applicable law.</li>
          <li>Reverse-engineering or scraping the Platform, or interfering with its operation.</li>
        </UL>

        <H2>6. Fees & Payment</H2>
        <P>
          Access to the Platform is provided under a separate commercial agreement between Corpzo and your AIF or Investment
          Manager. Overdue amounts may lead to suspension of access. Fees are non-refundable unless expressly agreed in writing.
        </P>

        <H2>7. Regulatory Compliance</H2>
        <P>
          You represent that your fund is validly registered with SEBI (where applicable) and that all operational actions
          you take on the Platform comply with the SEBI (Alternative Investment Funds) Regulations, 2012, the Prevention of
          Money Laundering Act, 2002, applicable tax laws, and other Indian regulations. Corpzo provides tools to help with
          these obligations but is not liable for your fund&apos;s regulatory outcomes.
        </P>

        <H2>8. Third-Party Integrations</H2>
        <P>
          The Platform integrates with third-party services (e.g., PAN verification, CKYC, e-sign, e-mail, payment gateways).
          Your use of those integrations is subject to the respective providers&apos; terms. Corpzo is not responsible for
          third-party outages or data-processing decisions.
        </P>

        <H2>9. Limitation of Liability</H2>
        <P>
          To the maximum extent permitted by law, Corpzo shall not be liable for indirect, incidental, special or consequential
          damages, including loss of profit, data, or business opportunity. Our aggregate liability in any 12-month period is
          limited to the fees paid by your fund to Corpzo in that period.
        </P>

        <H2>10. Termination</H2>
        <P>
          Corpzo may suspend or terminate access for material breach of these terms or for non-payment of fees. Upon termination,
          your fund will be provided a reasonable window (typically 30 days) to export data. Post-termination, we may retain
          data only as required by law or for legitimate audit purposes.
        </P>

        <H2>11. Changes to Terms</H2>
        <P>
          We may update these terms from time to time. Material changes will be notified in-product or by email at least 15
          days in advance. Continued use of the Platform after the effective date constitutes acceptance.
        </P>

        <H2>12. Governing Law & Jurisdiction</H2>
        <P>
          These terms are governed by the laws of India. Courts at Gautam Budh Nagar, Uttar Pradesh shall have exclusive
          jurisdiction over disputes arising from or in connection with the Platform.
        </P>

        <H2>13. Contact</H2>
        <P>
          Questions about these terms? Email <a href="mailto:reach@aifledger.com" style={{ color: NAVY, fontWeight: 600 }}>reach@aifledger.com</a> or
          write to Ultiwise Ventures Private Limited, Noida, India.
        </P>

        <div className="mt-16 pt-8 border-t text-xs" style={{ borderColor: BORDER, color: MUTED }}>
          © {new Date().getFullYear()} Ultiwise Ventures Private Limited · <Link to="/privacy" className="hover:text-[#0b192c]" data-testid="terms-privacy-link">Privacy Policy</Link> · <Link to="/" className="hover:text-[#0b192c]">Home</Link>
        </div>
      </main>
    </div>
  );
}
